import { Clipboard, Save, SquareArrowOutUpRight, Trash2, X } from "lucide-react";

export type GoogleCalendarEventLike = {
  EventId: string;
  Summary: string;
  SourceName?: string;
  Description?: string | null;
  Location?: string | null;
  ColorId?: string | null;
  Attendees?: string[];
  HtmlLink?: string | null;
  Status?: string | null;
  StartAt?: string | null;
  EndAt?: string | null;
  IsAllDay: boolean;
  ReminderUseDefault?: boolean;
  ReminderEnabled?: boolean;
  ReminderMethod?: "popup" | "email" | null;
  ReminderMinutes?: number | null;
  RecurrenceType?: "None" | "Daily" | "Weekly" | "Monthly" | "Yearly" | null;
  RecurrenceUntil?: string | null;
  Transparency?: "opaque" | "transparent" | null;
  Visibility?: "default" | "public" | "private" | "confidential" | null;
  GuestsCanInviteOthers?: boolean;
  GuestsCanModify?: boolean;
  GuestsCanSeeOtherGuests?: boolean;
  HasGoogleMeet?: boolean;
  HangoutLink?: string | null;
  UpdatedAt?: string | null;
};

export type GoogleCalendarEventForm = {
  EventId?: string;
  Summary: string;
  Description: string;
  Location: string;
  StartAt: string;
  EndAt: string;
  IsAllDay: boolean;
  ColorId: string;
  Attendees: string;
  ReminderUseDefault: boolean;
  ReminderEnabled: boolean;
  ReminderMethod: "popup" | "email";
  ReminderMinutes: number;
  RecurrenceType: "None" | "Daily" | "Weekly" | "Monthly" | "Yearly";
  RecurrenceUntil: string;
  Transparency: "opaque" | "transparent";
  Visibility: "default" | "public" | "private" | "confidential";
  GuestsCanInviteOthers: boolean;
  GuestsCanModify: boolean;
  GuestsCanSeeOtherGuests: boolean;
  CreateGoogleMeet: boolean;
  HasGoogleMeet: boolean;
  HangoutLink: string;
};

type GoogleCalendarEventFormFeatures = {
  Color: boolean;
  Attendees: boolean;
  Reminders: boolean;
  Recurrence: boolean;
  Visibility: boolean;
  GuestPermissions: boolean;
  Meet: boolean;
};

type GoogleCalendarEventFormModalProps = {
  form: GoogleCalendarEventForm;
  saving: boolean;
  features?: Partial<GoogleCalendarEventFormFeatures>;
  onChange: (patch: Partial<GoogleCalendarEventForm>) => void;
  onToggleAllDay: (checked: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  onMessage?: (message: string) => void;
};

type GoogleCalendarEventListProps<TEvent extends GoogleCalendarEventLike> = {
  events: TEvent[];
  loading: boolean;
  emptyMessage?: string;
  limit?: number;
  onEdit: (event: TEvent) => void;
};

type GoogleCalendarEventCalendarViewProps<TEvent extends GoogleCalendarEventLike> = {
  events: TEvent[];
  loading: boolean;
  rangeMode: "WEEK" | "MONTH" | "AGENDA";
  viewDate: Date;
  emptyMessage?: string;
  onToday: () => void;
  onMove: (direction: -1 | 1) => void;
  onEdit?: (event: TEvent) => void;
};

type GoogleCalendarEventPayload = Omit<GoogleCalendarEventForm, "EventId" | "HasGoogleMeet" | "HangoutLink"> & {
  Description: string | null;
  Location: string | null;
  ColorId: string | null;
  RecurrenceUntil: string | null;
};

export const googleCalendarColorOptions = [
  { Id: "", Label: "預設", Background: "#e9f4ec", Text: "#245438" },
  { Id: "1", Label: "薰衣草", Background: "#e8eaf6", Text: "#3f51b5" },
  { Id: "2", Label: "鼠尾草", Background: "#e3f4ea", Text: "#0b8043" },
  { Id: "3", Label: "葡萄紫", Background: "#f3e5f5", Text: "#8e24aa" },
  { Id: "4", Label: "珊瑚", Background: "#fdecea", Text: "#c53929" },
  { Id: "5", Label: "香蕉黃", Background: "#fff7d6", Text: "#9a6700" },
  { Id: "6", Label: "橘色", Background: "#feede5", Text: "#d04400" },
  { Id: "7", Label: "孔雀藍", Background: "#e0f2fb", Text: "#0277bd" },
  { Id: "8", Label: "石墨", Background: "#eeeeee", Text: "#424242" },
  { Id: "9", Label: "藍莓", Background: "#e8eaf6", Text: "#283593" },
  { Id: "10", Label: "羅勒綠", Background: "#e5f2ea", Text: "#0b6b3a" },
  { Id: "11", Label: "番茄紅", Background: "#fde7e7", Text: "#b00020" },
] as const;

export const basicGoogleCalendarEventFeatures: Partial<GoogleCalendarEventFormFeatures> = {
  Color: false,
  Attendees: false,
  Reminders: false,
  Recurrence: false,
  Visibility: false,
  GuestPermissions: false,
  Meet: true,
};

const defaultFeatures: GoogleCalendarEventFormFeatures = {
  Color: true,
  Attendees: true,
  Reminders: true,
  Recurrence: true,
  Visibility: true,
  GuestPermissions: true,
  Meet: true,
};

const formInputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";
const formLabelClass = "grid gap-1.5 text-xs font-bold text-[#59665f]";

export function googleCalendarColor(colorId?: string | null) {
  return googleCalendarColorOptions.find((option) => option.Id === (colorId || "")) || googleCalendarColorOptions[0];
}

function toDateTimeLocal(value: Date) {
  const offset = value.getTimezoneOffset() * 60000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 16);
}

function datePart(value: string) {
  return value.slice(0, 10);
}

function timePart(value: string) {
  return value.length >= 16 ? value.slice(11, 16) : "09:00";
}

function withDatePart(value: string, nextDate: string) {
  return value.length === 10 ? nextDate : `${nextDate}T${timePart(value)}`;
}

function withTimePart(value: string, nextTime: string) {
  return `${datePart(value)}T${nextTime}`;
}

export function defaultGoogleCalendarEventForm(): GoogleCalendarEventForm {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 1);
  const end = new Date(start);
  end.setHours(end.getHours() + 1);
  return {
    Summary: "",
    Description: "",
    Location: "",
    StartAt: toDateTimeLocal(start),
    EndAt: toDateTimeLocal(end),
    IsAllDay: false,
    ColorId: "",
    Attendees: "",
    ReminderUseDefault: true,
    ReminderEnabled: true,
    ReminderMethod: "popup",
    ReminderMinutes: 10,
    RecurrenceType: "None",
    RecurrenceUntil: "",
    Transparency: "opaque",
    Visibility: "default",
    GuestsCanInviteOthers: true,
    GuestsCanModify: false,
    GuestsCanSeeOtherGuests: true,
    CreateGoogleMeet: false,
    HasGoogleMeet: false,
    HangoutLink: "",
  };
}

export function googleCalendarEventToForm(event: GoogleCalendarEventLike): GoogleCalendarEventForm {
  return {
    EventId: event.EventId,
    Summary: event.Summary === "(未命名事件)" ? "" : event.Summary,
    Description: event.Description || "",
    Location: event.Location || "",
    StartAt: event.IsAllDay ? String(event.StartAt || "").slice(0, 10) : String(event.StartAt || "").slice(0, 16),
    EndAt: event.IsAllDay ? String(event.EndAt || "").slice(0, 10) : String(event.EndAt || "").slice(0, 16),
    IsAllDay: event.IsAllDay,
    ColorId: event.ColorId || "",
    Attendees: (event.Attendees || []).join("\n"),
    ReminderUseDefault: event.ReminderUseDefault ?? true,
    ReminderEnabled: event.ReminderEnabled ?? true,
    ReminderMethod: event.ReminderMethod || "popup",
    ReminderMinutes: event.ReminderMinutes || 10,
    RecurrenceType: event.RecurrenceType || "None",
    RecurrenceUntil: event.RecurrenceUntil || "",
    Transparency: event.Transparency || "opaque",
    Visibility: event.Visibility || "default",
    GuestsCanInviteOthers: event.GuestsCanInviteOthers ?? true,
    GuestsCanModify: event.GuestsCanModify ?? false,
    GuestsCanSeeOtherGuests: event.GuestsCanSeeOtherGuests ?? true,
    CreateGoogleMeet: Boolean(event.HasGoogleMeet),
    HasGoogleMeet: Boolean(event.HasGoogleMeet),
    HangoutLink: event.HangoutLink || "",
  };
}

export function googleCalendarEventPayloadFromForm(form: GoogleCalendarEventForm): GoogleCalendarEventPayload {
  return {
    Summary: form.Summary,
    Description: form.Description || null,
    Location: form.Location || null,
    StartAt: form.StartAt,
    EndAt: form.EndAt,
    IsAllDay: form.IsAllDay,
    ColorId: form.ColorId || null,
    Attendees: form.Attendees,
    ReminderUseDefault: form.ReminderUseDefault,
    ReminderEnabled: form.ReminderEnabled,
    ReminderMethod: form.ReminderMethod,
    ReminderMinutes: Number(form.ReminderMinutes) || 0,
    RecurrenceType: form.RecurrenceType,
    RecurrenceUntil: form.RecurrenceUntil || null,
    Transparency: form.Transparency,
    Visibility: form.Visibility,
    GuestsCanInviteOthers: form.GuestsCanInviteOthers,
    GuestsCanModify: form.GuestsCanModify,
    GuestsCanSeeOtherGuests: form.GuestsCanSeeOtherGuests,
    CreateGoogleMeet: form.CreateGoogleMeet && !form.HasGoogleMeet,
  };
}

export function toggleGoogleCalendarAllDayForm(form: GoogleCalendarEventForm, checked: boolean): GoogleCalendarEventForm {
  if (checked) {
    return { ...form, IsAllDay: true, StartAt: form.StartAt.slice(0, 10), EndAt: form.EndAt.slice(0, 10) };
  }
  return {
    ...form,
    IsAllDay: false,
    StartAt: form.StartAt.length === 10 ? `${form.StartAt}T09:00` : form.StartAt,
    EndAt: form.EndAt.length === 10 ? `${form.EndAt}T10:00` : form.EndAt,
  };
}

export function formatGoogleCalendarEventTime(event: GoogleCalendarEventLike) {
  if (!event.StartAt) return "未設定時間";
  if (event.IsAllDay) return `${event.StartAt.slice(0, 10)} 全天`;
  return new Date(event.StartAt).toLocaleString("zh-TW", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function dateKey(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function monthGridDays(value: Date) {
  const first = new Date(value.getFullYear(), value.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function weekDays(value: Date) {
  const start = new Date(value);
  start.setDate(value.getDate() - value.getDay());
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function eventDateRange(event: GoogleCalendarEventLike) {
  if (!event.StartAt) return null;
  const start = event.IsAllDay ? new Date(`${event.StartAt.slice(0, 10)}T00:00:00`) : new Date(event.StartAt);
  const endValue = event.EndAt || event.StartAt;
  const end = event.IsAllDay ? new Date(`${endValue.slice(0, 10)}T00:00:00`) : new Date(endValue);
  if (event.IsAllDay) {
    end.setDate(end.getDate() - 1);
  }
  return { start, end };
}

function eventOccursOn(event: GoogleCalendarEventLike, day: Date) {
  const range = eventDateRange(event);
  if (!range) return false;
  const current = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const start = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate());
  const end = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate());
  return current >= start && current <= end;
}

function eventsForDay<TEvent extends GoogleCalendarEventLike>(events: TEvent[], day: Date) {
  return events.filter((event) => eventOccursOn(event, day));
}

function monthLabel(value: Date) {
  return value.toLocaleDateString("zh-TW", { year: "numeric", month: "long" });
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const input = document.createElement("input");
  input.value = text;
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  input.remove();
}

export function GoogleCalendarEventList<TEvent extends GoogleCalendarEventLike>({ events, loading, emptyMessage = "目前範圍內沒有事件。", limit = 8, onEdit }: GoogleCalendarEventListProps<TEvent>) {
  if (loading) {
    return <div className="rounded-md border border-dashed border-[#cfd8d2] px-4 py-6 text-center text-sm text-[#7b877f]">Google 事件載入中...</div>;
  }
  if (events.length === 0) {
    return <div className="rounded-md border border-dashed border-[#cfd8d2] px-4 py-6 text-center text-sm text-[#7b877f]">{emptyMessage}</div>;
  }
  return (
    <div className="space-y-2">
      {events.slice(0, limit).map((event) => (
        <button className="flex w-full flex-col rounded-md border border-[#edf0ee] p-3 text-left hover:bg-[#f8faf8] sm:flex-row sm:items-center sm:justify-between" key={event.EventId} onClick={() => onEdit(event)} type="button">
          <span className="text-sm font-extrabold text-[#203d32]">{event.Summary}</span>
          <span className="mt-1 text-xs font-bold text-[#7b877f] sm:mt-0">{formatGoogleCalendarEventTime(event)}</span>
        </button>
      ))}
    </div>
  );
}

export function GoogleCalendarEventCalendarView<TEvent extends GoogleCalendarEventLike>({ events, loading, rangeMode, viewDate, emptyMessage = "目前範圍內沒有事件。", onToday, onMove, onEdit }: GoogleCalendarEventCalendarViewProps<TEvent>) {
  const monthDays = monthGridDays(viewDate);
  const currentWeekDays = weekDays(viewDate);
  const sortedEvents = [...events].sort((a, b) => String(a.StartAt || "").localeCompare(String(b.StartAt || "")));

  function renderEventButton(event: TEvent, compact = false) {
    const color = googleCalendarColor(event.ColorId);
    return (
      <button
        className={`${compact ? "block w-full truncate rounded px-2 py-1 text-left text-xs" : "w-full rounded-md border border-transparent p-2 text-left"} font-bold shadow-sm hover:border-[#cfd8d2]`}
        key={event.EventId}
        onClick={() => onEdit?.(event)}
        style={{ backgroundColor: color.Background, color: color.Text }}
        type="button"
      >
        {!event.IsAllDay && event.StartAt && compact ? `${new Date(event.StartAt).toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit" })} ` : ""}
        {event.SourceName && <span className="mr-1 opacity-80">[{event.SourceName}]</span>}
        {event.Summary}
        {!compact && <p className="mt-1 text-[11px] font-bold text-[#7b877f]">{formatGoogleCalendarEventTime(event)}</p>}
      </button>
    );
  }

  return (
    <div className="rounded-md border border-[#dfe6e1] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#edf0ee] bg-[#f8faf8] px-4 py-3">
        <div className="flex items-center gap-2">
          <button className="h-8 rounded-full border border-[#cfd8d2] px-4 text-sm font-bold text-[#315746] hover:bg-white" onClick={onToday} type="button">
            今天
          </button>
          <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#315746] hover:bg-white" onClick={() => onMove(-1)} type="button">
            ‹
          </button>
          <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#315746] hover:bg-white" onClick={() => onMove(1)} type="button">
            ›
          </button>
          <h3 className="ml-2 text-lg font-extrabold text-[#203d32]">{monthLabel(viewDate)}</h3>
        </div>
        <span className="text-xs font-bold text-[#7b877f]">{loading ? "Google 事件載入中" : "Google API 即時檢視"}</span>
      </div>
      {loading ? (
        <div className="flex min-h-[520px] items-center justify-center text-sm font-bold text-[#7b877f]">Google 事件載入中...</div>
      ) : rangeMode === "MONTH" ? (
        <div className="grid min-h-[620px] grid-cols-7 overflow-hidden text-sm">
          {["週日", "週一", "週二", "週三", "週四", "週五", "週六"].map((label) => (
            <div className="border-b border-[#edf0ee] bg-[#f8faf8] px-2 py-2 text-center text-xs font-extrabold text-[#59665f]" key={label}>
              {label}
            </div>
          ))}
          {monthDays.map((day) => {
            const dayEvents = eventsForDay(sortedEvents, day);
            const isCurrentMonth = day.getMonth() === viewDate.getMonth();
            const isToday = dateKey(day) === dateKey(new Date());
            return (
              <div className={`min-h-[104px] border-b border-r border-[#edf0ee] p-2 ${isCurrentMonth ? "bg-white" : "bg-[#f8faf8] text-[#9aa6a0]"}`} key={dateKey(day)}>
                <div className={`mb-1 inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-extrabold ${isToday ? "bg-[#315746] text-white" : "text-[#203d32]"}`}>{day.getDate()}</div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((event) => renderEventButton(event, true))}
                  {dayEvents.length > 3 && <p className="text-xs font-bold text-[#7b877f]">+{dayEvents.length - 3} 筆</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : rangeMode === "WEEK" ? (
        <div className="grid min-h-[520px] gap-3 p-4 md:grid-cols-7">
          {currentWeekDays.map((day) => {
            const dayEvents = eventsForDay(sortedEvents, day);
            const isToday = dateKey(day) === dateKey(new Date());
            return (
              <div className="rounded-md border border-[#edf0ee] bg-[#f8faf8] p-3" key={dateKey(day)}>
                <div className={`mb-3 rounded-md px-2 py-1 text-center text-sm font-extrabold ${isToday ? "bg-[#315746] text-white" : "bg-white text-[#203d32]"}`}>
                  {day.toLocaleDateString("zh-TW", { weekday: "short", month: "2-digit", day: "2-digit" })}
                </div>
                <div className="space-y-2">
                  {dayEvents.length === 0 ? <p className="text-center text-xs font-medium text-[#9aa6a0]">無事件</p> : dayEvents.map((event) => renderEventButton(event))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="min-h-[520px] space-y-2 p-4">
          {sortedEvents.length === 0 ? (
            <div className="rounded-md border border-dashed border-[#cfd8d2] px-4 py-10 text-center text-sm text-[#7b877f]">{emptyMessage}</div>
          ) : (
            sortedEvents.map((event) => (
              <button className="flex w-full flex-col rounded-md border border-[#edf0ee] p-3 text-left hover:border-[#cfd8d2] sm:flex-row sm:items-center sm:justify-between" key={event.EventId} onClick={() => onEdit?.(event)} style={{ backgroundColor: googleCalendarColor(event.ColorId).Background }} type="button">
                <span className="text-sm font-extrabold text-[#203d32]">
                  {event.SourceName && <span className="mr-1 text-[#59665f]">[{event.SourceName}]</span>}
                  {event.Summary}
                </span>
                <span className="mt-1 text-xs font-bold text-[#7b877f] sm:mt-0">{formatGoogleCalendarEventTime(event)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function GoogleCalendarEventFormModal({ form, saving, features, onChange, onToggleAllDay, onSave, onCancel, onDelete, onMessage }: GoogleCalendarEventFormModalProps) {
  const enabledFeatures = { ...defaultFeatures, ...features };
  async function copyMeetLink() {
    try {
      await copyText(form.HangoutLink);
      onMessage?.("Google Meet 會議連結已複製。");
    } catch {
      onMessage?.("會議連結複製失敗，請手動開啟後複製。");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#203d32]/45 p-4" role="dialog" aria-modal="true">
      <div className="max-h-[calc(100svh-32px)] w-full max-w-3xl overflow-auto rounded-md bg-white shadow-[0_24px_70px_rgba(32,61,50,.28)]">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#dfe6e1] bg-white px-5 py-4">
          <h3 className="text-lg font-extrabold text-[#203d32]">{form.EventId ? "編輯事件" : "新增事件"}</h3>
          <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#59665f] hover:bg-[#f0f5f2]" onClick={onCancel} type="button">
            <X size={18} />
          </button>
        </div>
        <div className="grid gap-5 p-5">
          <input className={formInputClass} onChange={(event) => onChange({ Summary: event.target.value })} placeholder="事件標題" value={form.Summary} />
          <label className="flex items-center gap-2 text-sm font-bold text-[#59665f]">
            <input checked={form.IsAllDay} className="h-4 w-4 accent-[#315746]" onChange={(event) => onToggleAllDay(event.target.checked)} type="checkbox" />
            全天事件
          </label>
          <div className={form.IsAllDay ? "grid gap-3 sm:grid-cols-2" : "grid gap-3 sm:grid-cols-2 xl:grid-cols-4"}>
            {form.IsAllDay ? (
              <>
                <label className={formLabelClass}>
                  開始日期
                  <input className={formInputClass} onChange={(event) => onChange({ StartAt: event.target.value })} type="date" value={form.StartAt} />
                </label>
                <label className={formLabelClass}>
                  結束日期
                  <input className={formInputClass} onChange={(event) => onChange({ EndAt: event.target.value })} type="date" value={form.EndAt} />
                </label>
              </>
            ) : (
              <>
                <label className={formLabelClass}>
                  開始日期
                  <input className={formInputClass} onChange={(event) => onChange({ StartAt: withDatePart(form.StartAt, event.target.value) })} type="date" value={datePart(form.StartAt)} />
                </label>
                <label className={formLabelClass}>
                  開始時間
                  <input className={formInputClass} onChange={(event) => onChange({ StartAt: withTimePart(form.StartAt, event.target.value) })} type="time" value={timePart(form.StartAt)} />
                </label>
                <label className={formLabelClass}>
                  結束日期
                  <input className={formInputClass} onChange={(event) => onChange({ EndAt: withDatePart(form.EndAt, event.target.value) })} type="date" value={datePart(form.EndAt)} />
                </label>
                <label className={formLabelClass}>
                  結束時間
                  <input className={formInputClass} onChange={(event) => onChange({ EndAt: withTimePart(form.EndAt, event.target.value) })} type="time" value={timePart(form.EndAt)} />
                </label>
              </>
            )}
          </div>
          {enabledFeatures.Color && (
            <section className="grid gap-3 rounded-md border border-[#edf0ee] p-4">
              <h4 className="text-sm font-extrabold text-[#203d32]">顏色</h4>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                {googleCalendarColorOptions.map((option) => (
                  <button
                    className={`flex h-10 items-center justify-center rounded-md border text-xs font-bold ${form.ColorId === option.Id ? "border-[#315746] ring-2 ring-[#315746]/20" : "border-[#dfe6e1]"}`}
                    key={option.Id || "default"}
                    onClick={() => onChange({ ColorId: option.Id })}
                    style={{ backgroundColor: option.Background, color: option.Text }}
                    type="button"
                  >
                    {option.Label}
                  </button>
                ))}
              </div>
            </section>
          )}
          <div className={enabledFeatures.Attendees ? "grid gap-4 lg:grid-cols-2" : "grid gap-4"}>
            <label className={formLabelClass}>
              地點
              <input className={formInputClass} onChange={(event) => onChange({ Location: event.target.value })} placeholder="地點（選填）" value={form.Location} />
            </label>
            {enabledFeatures.Attendees && (
              <label className={formLabelClass}>
                參與者 Email
                <textarea className="min-h-[92px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" onChange={(event) => onChange({ Attendees: event.target.value })} placeholder="可用換行、逗號或分號分隔" value={form.Attendees} />
              </label>
            )}
          </div>
          <label className={formLabelClass}>
            備註
            <textarea className="min-h-[120px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" onChange={(event) => onChange({ Description: event.target.value })} placeholder="備註（選填）" value={form.Description} />
          </label>
          {(enabledFeatures.Meet || enabledFeatures.Reminders) && (
            <section className="grid gap-3 rounded-md border border-[#edf0ee] p-4">
              <h4 className="text-sm font-extrabold text-[#203d32]">視訊與提醒</h4>
              {enabledFeatures.Meet && (
                <>
                  <label className="flex items-center gap-2 text-sm font-bold text-[#59665f]">
                    <input checked={form.CreateGoogleMeet} className="h-4 w-4 accent-[#315746]" onChange={(event) => onChange({ CreateGoogleMeet: event.target.checked })} type="checkbox" />
                    建立 Google Meet 會議連結
                  </label>
                  {form.HangoutLink && (
                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-[#dfe6e1] bg-[#f8faf8] p-2">
                      <span className="min-w-0 flex-1 truncate px-1 text-sm font-bold text-[#59665f]">{form.HangoutLink}</span>
                      <button className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={copyMeetLink} type="button">
                        <Clipboard size={14} />
                        複製連結
                      </button>
                      <a className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" href={form.HangoutLink} rel="noreferrer" target="_blank">
                        開啟
                        <SquareArrowOutUpRight size={14} />
                      </a>
                    </div>
                  )}
                </>
              )}
              {enabledFeatures.Reminders && (
                <>
                  <label className="flex items-center gap-2 text-sm font-bold text-[#59665f]">
                    <input checked={form.ReminderUseDefault} className="h-4 w-4 accent-[#315746]" onChange={(event) => onChange({ ReminderUseDefault: event.target.checked })} type="checkbox" />
                    使用 Google 行事曆預設提醒
                  </label>
                  {!form.ReminderUseDefault && (
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_150px_150px]">
                      <label className="flex items-center gap-2 text-sm font-bold text-[#59665f]">
                        <input checked={form.ReminderEnabled} className="h-4 w-4 accent-[#315746]" onChange={(event) => onChange({ ReminderEnabled: event.target.checked })} type="checkbox" />
                        啟用自訂提醒
                      </label>
                      <label className={formLabelClass}>
                        提醒方式
                        <select className={formInputClass} disabled={!form.ReminderEnabled} onChange={(event) => onChange({ ReminderMethod: event.target.value as GoogleCalendarEventForm["ReminderMethod"] })} value={form.ReminderMethod}>
                          <option value="popup">通知</option>
                          <option value="email">Email</option>
                        </select>
                      </label>
                      <label className={formLabelClass}>
                        提前分鐘
                        <input className={formInputClass} disabled={!form.ReminderEnabled} min={0} max={40320} onChange={(event) => onChange({ ReminderMinutes: Number(event.target.value) })} type="number" value={form.ReminderMinutes} />
                      </label>
                    </div>
                  )}
                </>
              )}
            </section>
          )}
          {(enabledFeatures.Recurrence || enabledFeatures.Visibility) && (
            <section className="grid gap-3 rounded-md border border-[#edf0ee] p-4">
              <h4 className="text-sm font-extrabold text-[#203d32]">重複與顯示</h4>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {enabledFeatures.Recurrence && (
                  <>
                    <label className={formLabelClass}>
                      重複
                      <select className={formInputClass} onChange={(event) => onChange({ RecurrenceType: event.target.value as GoogleCalendarEventForm["RecurrenceType"] })} value={form.RecurrenceType}>
                        <option value="None">不重複</option>
                        <option value="Daily">每天</option>
                        <option value="Weekly">每週</option>
                        <option value="Monthly">每月</option>
                        <option value="Yearly">每年</option>
                      </select>
                    </label>
                    <label className={formLabelClass}>
                      重複到
                      <input className={formInputClass} disabled={form.RecurrenceType === "None"} onChange={(event) => onChange({ RecurrenceUntil: event.target.value })} type="date" value={form.RecurrenceUntil} />
                    </label>
                  </>
                )}
                {enabledFeatures.Visibility && (
                  <>
                    <label className={formLabelClass}>
                      顯示為
                      <select className={formInputClass} onChange={(event) => onChange({ Transparency: event.target.value as GoogleCalendarEventForm["Transparency"] })} value={form.Transparency}>
                        <option value="opaque">忙碌</option>
                        <option value="transparent">空閒</option>
                      </select>
                    </label>
                    <label className={formLabelClass}>
                      隱私
                      <select className={formInputClass} onChange={(event) => onChange({ Visibility: event.target.value as GoogleCalendarEventForm["Visibility"] })} value={form.Visibility}>
                        <option value="default">預設</option>
                        <option value="public">公開</option>
                        <option value="private">私人</option>
                        <option value="confidential">機密</option>
                      </select>
                    </label>
                  </>
                )}
              </div>
            </section>
          )}
          {enabledFeatures.GuestPermissions && (
            <section className="grid gap-3 rounded-md border border-[#edf0ee] p-4">
              <h4 className="text-sm font-extrabold text-[#203d32]">來賓權限</h4>
              <div className="grid gap-2 sm:grid-cols-3">
                <label className="flex items-center gap-2 text-sm font-bold text-[#59665f]">
                  <input checked={form.GuestsCanInviteOthers} className="h-4 w-4 accent-[#315746]" onChange={(event) => onChange({ GuestsCanInviteOthers: event.target.checked })} type="checkbox" />
                  可邀請其他人
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-[#59665f]">
                  <input checked={form.GuestsCanModify} className="h-4 w-4 accent-[#315746]" onChange={(event) => onChange({ GuestsCanModify: event.target.checked })} type="checkbox" />
                  可修改事件
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-[#59665f]">
                  <input checked={form.GuestsCanSeeOtherGuests} className="h-4 w-4 accent-[#315746]" onChange={(event) => onChange({ GuestsCanSeeOtherGuests: event.target.checked })} type="checkbox" />
                  可查看來賓
                </label>
              </div>
            </section>
          )}
        </div>
        <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-[#dfe6e1] bg-white px-5 py-4">
          {form.EventId && onDelete ? (
            <button className="inline-flex h-10 items-center gap-1.5 rounded-md border border-[#f2b8b5] bg-white px-3 text-sm font-bold text-[#8a1f1f] hover:bg-[#fdebea] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} onClick={onDelete} type="button">
              <Trash2 size={15} />
              刪除事件
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#59665f] hover:bg-[#f0f5f2]" onClick={onCancel} type="button">
              取消
            </button>
            <button className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md bg-[#315746] px-4 text-sm font-bold text-white transition hover:bg-[#254536] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} onClick={onSave} type="button">
              <Save size={15} />
              {saving ? "儲存中" : "儲存到 Google 行事曆"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
